<script setup lang="ts">
import { computed, ref } from "vue";
import IncidentIcon from "@component/IncidentIcon.vue";
import IncidentLevel from "@dashboard/base/IncidentLevel";
import type { IncidentAlertsMap } from "@entity/Incident";
import type { ServiceReportItem, ServiceRowGroupProps } from "@entity/ServiceRow";

defineOptions({
  inheritAttrs: false,
});

interface Props extends ServiceRowGroupProps {
  id: string;
  text: string;
  regionNames: string[];
}

interface RowGroupRegion {
  ids: number[];
  region: string;
}

const props = defineProps<Props>();

// Match Vue 2: groups start collapsed and expand only through user interaction.
const expanded = ref(false);

function getReportItem(id: number): ServiceReportItem | undefined {
  return props.report[id];
}

function expand(): void {
  expanded.value = true;
}

function maxLevel(ids: number[]): string {
  const levels = ids.map((id) => getReportItem(id)?.level ?? IncidentLevel.Good);
  return IncidentLevel.max(levels);
}

function combineAlerts(ids: number[]): IncidentAlertsMap {
  const result: IncidentAlertsMap = {};

  ids.forEach((id) => {
    const alerts = getReportItem(id)?.alerts;
    if (alerts) {
      Object.assign(result, alerts);
    }
  });

  return result;
}

const regions = computed<RowGroupRegion[]>(() =>
  props.regionNames.map((region, index) => ({
    ids: (props.ids[index] ?? []).filter((id) => getReportItem(id) !== undefined),
    region: region.toLowerCase(),
  })),
);

</script>

<template>
  <div class="big-block CId-row-group IId-row-group">
    <input
      :id="props.id"
      v-model="expanded"
      type="checkbox"
      data-dtm-element="row-group"
      :data-dtm-name="props.text"
    >
    <label :for="props.id"><span />{{ props.text }}</label>
    <div class="block-closed" @click.prevent="expand">
      <template v-for="group in regions" :key="group.region">
        <IncidentIcon
          v-if="group.ids.length"
          :service="props.service"
          :level="maxLevel(group.ids)"
          :alerts="combineAlerts(group.ids)"
          :region="group.region"
        />
        <div v-else class="have-info-wrapper left large" aria-hidden="true" />
      </template>
    </div>
    <div class="block-opened">
      <slot />
    </div>
  </div>
</template>
